"use client";

import type { ReactNode } from 'react';

import useInteractionTracker from '@blueshift-ui/telemetry/dist/hooks/use-interaction-tracker';
import { BookOpen, Eye, EyeOff, Lightbulb } from 'lucide-react';
import { useState } from 'react';

import type { DifficultyLevel } from '../constants';
import type { VideoProps } from '../types/video';

import DifficultyBadge from './difficulty-badge';
import VideoSection from './video-section';

interface QuestionExerciseProps extends VideoProps {
  buttonText?: {
    show?: string;
    hide?: string;
  };
  difficulty: DifficultyLevel;
  problemNumber: number;
  scenario: {
    title: string;
    description: ReactNode;
    icon?: string;
  };
  solution: {
    description: ReactNode;
  };
  title: string;
}

const QuestionExercise = ({
  buttonText = { hide: 'Hide Answer', show: 'Show Answer' },
  difficulty,
  problemNumber,
  scenario,
  solution,
  title,
  videoData,
  videoId,
}: QuestionExerciseProps) => {
  const [showAnswer, setShowAnswer] = useState(false);
  const { trackEvent } = useInteractionTracker();

  // Get difficulty-based styling for problem number circle
  const getDifficultyCircleStyle = (difficulty: DifficultyLevel) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-500 border-2 border-green-400';
      case 'Intermediate':
        return 'bg-amber-500 border-2 border-amber-400';
      case 'Advanced':
        return 'bg-red-500 border-2 border-red-400';
      default:
        return 'bg-green-500 border-2 border-green-400';
    }
  };

  // Get difficulty-based stripe color
  const getDifficultyStripe = (difficulty: DifficultyLevel) => {
    switch (difficulty) {
      case 'Beginner':
        return 'from-green-500 to-green-400';
      case 'Intermediate':
        return 'from-amber-500 to-amber-400';
      case 'Advanced':
        return 'from-red-500 to-red-400';
      default:
        return 'from-emerald-500 to-emerald-400';
    }
  };

  const toggleAnswer = () => {
    const next = !showAnswer;
    setShowAnswer(next);
    void trackEvent({
      action: next ? 'show-answer' : 'hide-answer',
      category: 'exercise',
      label: `problem-${problemNumber}`,
    });
  };

  return (
    <div className="surface-card rounded-xl shadow-lg mb-8 overflow-hidden hover:shadow-xl transition-shadow">
      <div
        className={`h-2 bg-gradient-to-r ${getDifficultyStripe(difficulty)}`}
      />
      <div className="border-b border-glass p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div
              className={`flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl text-white font-bold text-base sm:text-lg flex-shrink-0 ${getDifficultyCircleStyle(difficulty)}`}
            >
              {problemNumber}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-xl font-bold text-primary mb-1 truncate">
                {title}
              </h3>
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="text-secondary">Question Exercise</span>
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <DifficultyBadge difficulty={difficulty} />
          </div>
        </div>
      </div>

      <div className="p-6">
        {videoData && videoId && videoData.src && (
          <VideoSection videoData={videoData} videoId={videoId} />
        )}

        <div className="explanation-box mb-6 no-math">
          <div className="flex items-start gap-3">
            <BookOpen aria-hidden="true" className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-primary mb-3">
                {scenario.title}
              </h4>
              <div className="text-secondary leading-relaxed">
                {scenario.description}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-6">
          <button
            aria-controls="question-answer"
            aria-describedby="question-instructions"
            aria-expanded={showAnswer}
            aria-label={`${showAnswer ? buttonText.hide : buttonText.show} - ${showAnswer ? 'Hide' : 'Show'} the solution for this question exercise`}
            className="cta-button-primary"
            onClick={toggleAnswer}
            type="button"
          >
            {showAnswer ? (
              <EyeOff aria-hidden="true" className="w-4 h-4" />
            ) : (
              <Eye aria-hidden="true" className="w-4 h-4" />
            )}
            {showAnswer ? buttonText.hide : buttonText.show}
          </button>
        </div>

        {/* Hidden instructions for screen readers */}
        <div className="sr-only" id="question-instructions">
          Click to {showAnswer ? 'hide' : 'reveal'} the detailed solution for this question exercise.
        </div>

        {/* Screen reader announcement for answer reveal */}
        <div
          aria-live="polite"
          className="sr-only"
        >
          {showAnswer && "Answer explanation is now visible."}
        </div>

        {showAnswer && (
          <div className="mt-6 solution-box animate-fade-in-scale-bounce" id="question-answer">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-exercise-question mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-primary mb-4">Explanation</h4>
                <div className="text-secondary leading-relaxed">
                  {solution.description}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionExercise;
